import React, { useRef, useEffect, useState } from 'react';

/**
 * SearchInputWithHighlighting component
 * Renders a search input field with syntax highlighting for operators like from:, is:, etc.
 */
const SearchInputWithHighlighting = ({
  value,
  onChange,
  onClick,
  onKeyDown,
  onBlur,
  placeholder,
  className,
  ariaLabel,
  inputRef
}) => {
  const containerRef = useRef(null);
  const highlightRef = useRef(null);
  const [showingPlaceholder, setShowingPlaceholder] = useState(!value);

  // Event handler functions
  const handleInputChange = (e) => {
    if (onChange) {
      onChange(e);
    }
  };
  
  const handleInputClick = (e) => {
    if (onClick) {
      onClick(e);
    }
  };
  
  const handleInputKeyDown = (e) => {
    if (onKeyDown) {
      onKeyDown(e);
    }
  };
  
  const handleInputBlur = (e) => {
    if (onBlur) {
      onBlur(e);
    }
  };

  // Parse a search query into operator, value, and text segments
  const parseSearchQuery = (query) => {
    const operators = ['from:', 'is:', 'subreddit:', 'not:'];
    const segments = [];
    
    let currentWord = '';
    let currentType = 'text';
    let inQuotes = false;
    
    // Helper to add current word as a segment
    const addCurrentWord = () => {
      if (currentWord) {
        segments.push({
          type: currentType,
          text: currentWord
        });
        currentWord = '';
        currentType = 'text';
      }
    };
    
    // Process the query character by character
    for (let i = 0; i < query.length; i++) {
      const char = query[i];
      
      // Handle quotes
      if (char === '"') {
        currentWord += char;
        inQuotes = !inQuotes;
        continue;
      }
      
      // If in quotes, just add the character
      if (inQuotes) {
        currentWord += char;
        continue;
      }
      
      // Check for spaces (word boundaries)
      if (char === ' ') {
        addCurrentWord();
        segments.push({ type: 'text', text: ' ' });
        continue;
      }
      
      // Check for operators at the beginning of a word
      if (currentWord === '') {
        let isOperator = false;
        for (const op of operators) {
          if (query.substring(i).startsWith(op)) {
            addCurrentWord();
            segments.push({ type: 'operator', text: op });
            i += op.length - 1; // Skip the operator length
            isOperator = true;
            currentType = 'value'; // The next word will be a value
            break;
          }
        }
        if (isOperator) continue;
      }
      
      // Add the character to the current word
      currentWord += char;
    }
    
    // Add any remaining text
    addCurrentWord();
    
    return segments;
  };

  // Update the highlighted text whenever the value changes
  useEffect(() => {
    if (!highlightRef.current) return;
    
    // Set placeholder visibility
    setShowingPlaceholder(!value);
    
    // Skip highlighting if empty (placeholder will be shown instead)
    if (!value) {
      highlightRef.current.innerHTML = '';
      return;
    }
    
    // Skip intensive highlighting operations during rapid typing of operators
    if (value.endsWith(':') && !value.includes(' ')) {
      // User is typing an operator, just show the plain text
      highlightRef.current.textContent = value;
      return;
    }
    
    // Clear any existing content
    highlightRef.current.innerHTML = '';
    
    // Parse the input into highlighted segments
    const segments = parseSearchQuery(value);
    
    // Add each segment to the highlighting layer
    segments.forEach(segment => {
      const span = document.createElement('span');
      span.textContent = segment.text;
      
      if (segment.type === 'operator') {
        span.className = 'search-operator';
      } else if (segment.type === 'value') {
        span.className = 'search-value';
      }
      
      highlightRef.current.appendChild(span);
    });
    
    // Ensure the scroll position of the highlight layer matches the input
    if (inputRef.current) {
      highlightRef.current.scrollLeft = inputRef.current.scrollLeft;
    }
  }, [value]); // Removed inputRef dependency
  
  // Sync scrolling between input and highlighting
  const handleScroll = (e) => {
    if (highlightRef.current) {
      highlightRef.current.scrollLeft = e.target.scrollLeft;
    }
  };
  
  return (
    <div className="syntax-highlight-container" ref={containerRef}>
      {/* Syntax highlighting layer */}
      <div 
        className={`syntax-highlight-layer ${showingPlaceholder ? 'placeholder' : ''}`}
        ref={highlightRef}
        aria-hidden="true"
      >
        {showingPlaceholder ? placeholder : ''}
      </div>
      
      {/* Actual input element (on top, transparent) */}
      <input
        type="text"
        value={value}
        onChange={handleInputChange}
        onClick={handleInputClick}
        onKeyDown={handleInputKeyDown}
        onBlur={handleInputBlur}
        onScroll={handleScroll}
        ref={inputRef}
        className={`syntax-highlight-input ${className || ''}`}
        placeholder={placeholder}
        aria-label={ariaLabel}
      />
    </div>
  );
};

export default SearchInputWithHighlighting;